import { Fragment, useCallback, useMemo } from "react";
import ChartLegend from "components/charting/ChartLegend";
import { useChartCompareSetSelectContext } from "components/charting/contexts/ChartCompareSetSelectContext";
import { useChartSetSelectContext } from "components/charting/contexts/ChartSetSelectContext";
import { useChartProfileSelectContext } from "components/charting/contexts/ChartProfileSelectContext";
import { useChartCompareProfileSelectContext } from "components/charting/contexts/ChartCompareProfileSelectContext";
import usePreferences from "hooks/usePreferences";
import useColorRegistry from "features/Charting/hooks/useColorRegistry";
import type { MeasurementSet } from "types";
import type { LoadVelocityProfile } from "types/load-velocity-profile";
import LoadVelocityProfileHelpers from "helpers/LoadVelocityProfileHelpers";
import datetime from "config/datetime";
import Grid from "components/primitives/Grid";
import { firstBy } from "thenby";
import moment from "moment";
import Typography from "@mui/material/Typography";
import * as Components from "./AthleteProfilingChartLegend.components";
import { ProfilingChartEntityId } from "components/charting/types";
import SetComparisonChartService from "services/SetComparisonChartService";
import { Trans, useTranslation } from "react-i18next";
import Span from "components/primitives/Span";

interface AthleteProfilingChartLegendProps {}

function AthleteProfilingChartLegend(props: AthleteProfilingChartLegendProps) {
  const { value: selectedPrimarySets, onChange: onChangePrimarySets } = useChartSetSelectContext();
  const { value: selectedPrimaryProfiles, onChange: onChangePrimaryProfiles } = useChartProfileSelectContext();
  const { value: selectedComparativeSets, onChange: onChangeComparativeSets } = useChartCompareSetSelectContext();
  const { value: selectedComparativeProfiles, onChange: onChangeComparativeProfiles } =
    useChartCompareProfileSelectContext();

  const getColor = useColorRegistry();
  const [{ imperialSystemEnabled }] = usePreferences();
  const { t } = useTranslation();

  const makeLabel = useCallback(
    (set: MeasurementSet) => SetComparisonChartService.makeLabel(set, imperialSystemEnabled, t),
    [imperialSystemEnabled, t]
  );

  // Names the side in bold for a unilateral profile, e.g. "Left side: Latest profile (Mar 9, 2026)".
  const makeProfileLabel = useCallback(
    (profile: LoadVelocityProfile) => (
      <Trans
        i18nKey="athlete_profile.load_velocity_profile_option_label"
        context={profile.variant ?? undefined}
        values={{ date: datetime(profile.createdAt).format("ll") }}
        components={{ Bold: <Span sx={{ fontWeight: "fontWeightMedium" }} /> }}
      />
    ),
    []
  );

  const makeProfileTitle = useCallback(
    (profile: LoadVelocityProfile) =>
      t("athlete_profile.load_velocity_profile_option_label", {
        date: datetime(profile.createdAt).format("ll"),
        variant: t("measures.variant_abbr", { context: profile.variant }),
        context: profile.variant ? "unilateral" : undefined,
      }),
    [t]
  );

  const makeLegendItems = useCallback(
    (sets: MeasurementSet[], onChange?: (sets: MeasurementSet[]) => void, entityColor?: string) =>
      sets.map((set) => ({
        id: set.measurementId,
        color: entityColor || getColor(set.measurementId),
        label: makeLabel(set),
        remove: onChange ? () => onChange(sets.filter((s) => s !== set)) : undefined,
      })),
    [getColor, makeLabel]
  );

  const makeProfileLegendItems = useCallback(
    (profiles: LoadVelocityProfile[], entityColor: string, onChange?: (profiles: LoadVelocityProfile[]) => void) =>
      profiles.map((profile) => ({
        id: LoadVelocityProfileHelpers.getKey(profile),
        color: entityColor,
        label: makeProfileLabel(profile),
        title: makeProfileTitle(profile),
        remove: onChange ? () => onChange(profiles.filter((p) => p !== profile)) : undefined,
      })),
    [makeProfileLabel, makeProfileTitle]
  );

  const primaryLineColor = getColor(ProfilingChartEntityId.PROFILING_LINE);
  const comparativeLineColor = getColor(ProfilingChartEntityId.COMPARATIVE_LINE);

  const primarySetItems = useMemo(
    () => [
      ...makeProfileLegendItems(selectedPrimaryProfiles, primaryLineColor, onChangePrimaryProfiles),
      ...makeLegendItems(selectedPrimarySets, onChangePrimarySets),
    ],
    [
      makeProfileLegendItems,
      makeLegendItems,
      selectedPrimaryProfiles,
      primaryLineColor,
      onChangePrimaryProfiles,
      selectedPrimarySets,
      onChangePrimarySets,
    ]
  );

  const comparativeSetItems = useMemo(
    () => [
      ...makeProfileLegendItems(selectedComparativeProfiles, comparativeLineColor, onChangeComparativeProfiles),
      ...makeLegendItems(selectedComparativeSets, onChangeComparativeSets),
    ],
    [
      makeProfileLegendItems,
      makeLegendItems,
      selectedComparativeProfiles,
      comparativeLineColor,
      onChangeComparativeProfiles,
      selectedComparativeSets,
      onChangeComparativeSets,
    ]
  );

  const makeDateRangeLabel = useCallback((sets: MeasurementSet[]) => {
    const sortedSets = sets.slice(0).sort(firstBy("completedDate"));
    const startDate = moment(sortedSets[0]?.completedDate).format("MMM D, YYYY");
    const endDate = moment(sortedSets[sortedSets.length - 1]?.completedDate).format("MMM D, YYYY");

    if (startDate === endDate) return startDate;

    return `${startDate} - ${endDate}`;
  }, []);

  const renderTooltip = useCallback(
    (sets: MeasurementSet[], profiles: LoadVelocityProfile[], entityColor: string) => (
      <Grid container position="relative" textAlign="left">
        <Grid pl={1} width="100%">
          <ChartLegend
            items={[...makeProfileLegendItems(profiles, entityColor), ...makeLegendItems(sets, undefined, entityColor)]}
            disableBorders
          />
        </Grid>
      </Grid>
    ),
    [makeLegendItems, makeProfileLegendItems]
  );

  // A profile replaces the sets on its side, so it labels that side instead of a date range.
  const makeSideLabel = useCallback(
    (sets: MeasurementSet[], profiles: LoadVelocityProfile[]) =>
      profiles.length
        ? profiles.map((profile, index) => (
            <Fragment key={LoadVelocityProfileHelpers.getKey(profile)}>
              {index > 0 && ", "}
              {makeProfileLabel(profile)}
            </Fragment>
          ))
        : makeDateRangeLabel(sets),
    [makeDateRangeLabel, makeProfileLabel]
  );

  const primarySetsLabel = makeSideLabel(selectedPrimarySets, selectedPrimaryProfiles);
  const comparativeSetsLabel = makeSideLabel(selectedComparativeSets, selectedComparativeProfiles);

  if (primarySetItems.length === 0 && comparativeSetItems.length === 0) {
    return null;
  }

  if (primarySetItems.length !== 0 && comparativeSetItems.length === 0) {
    return <ChartLegend items={primarySetItems} />;
  }

  if (primarySetItems.length === 0 && comparativeSetItems.length !== 0) {
    return <ChartLegend items={comparativeSetItems} />;
  }

  return (
    <Grid container spacing={1} justifyContent="center">
      <Components.ComparativeProfilingChartTooltip
        arrow
        title={renderTooltip(selectedPrimarySets, selectedPrimaryProfiles, primaryLineColor)}
      >
        <Grid
          size={{ xs: 6, lg: 4, xl: 4 }}
          maxWidth={280}
          borderRadius="4px"
          border={`1px solid`}
          borderColor={"grey.300"}
          m="0px"
          p="0px"
        >
          <Grid container alignItems="center" spacing={"0px"} m="0px" p="0px">
            <Grid px={1}>
              <Components.ColorBlock color={primaryLineColor} />
            </Grid>
            <Grid size="grow">
              <Typography sx={{ fontWeight: 500 }}>{t("global.baseline_sets")}</Typography> {primarySetsLabel}
            </Grid>
          </Grid>
        </Grid>
      </Components.ComparativeProfilingChartTooltip>
      <Grid p={0.5} />
      <Components.ComparativeProfilingChartTooltip
        arrow
        title={renderTooltip(selectedComparativeSets, selectedComparativeProfiles, comparativeLineColor)}
      >
        <Grid
          size={{ xs: 6, lg: 4, xl: 4 }}
          maxWidth={280}
          borderRadius="4px"
          border={`1px solid`}
          borderColor={"grey.300"}
          m="0px"
          p="0px"
        >
          <Grid container alignItems="center" spacing={"0px"} m="0px" p="0px">
            <Grid px={1}>
              <Components.ColorBlock color={comparativeLineColor} />
            </Grid>
            <Grid size="grow">
              <Typography sx={{ fontWeight: 500 }}>{t("global.comparison_sets")}</Typography> {comparativeSetsLabel}
            </Grid>
          </Grid>
        </Grid>
      </Components.ComparativeProfilingChartTooltip>
    </Grid>
  );
}

export default AthleteProfilingChartLegend;
